/**
 * The item form's attachment rules (planner collab materials R1, R4, R5, R7). Pure: the form
 * and the day boxes call these; the requests live in `frontend/api.ts`.
 */
import { materialFormat } from './collab';
import { MAX_ITEM_MATERIALS } from './days';
import { COLLAB_ORIGIN } from './handoff';
import type { ItemMaterial } from './types';

/** What the file picker offers: Collab's own list (`packages/collab/src/upload-copy.ts`). */
export const UPLOAD_ACCEPT = '.pdf,.docx,.pptx,.png,.jpg,.jpeg,.txt';
/** Collab's file cap, 10 MB (R5). Collab decides again from the bytes either way. */
export const MAX_UPLOAD_FILE_BYTES = 10 * 1024 * 1024;
/** Collab's longest material title. */
const MAX_UPLOAD_TITLE = 200;

/** A material's page in Collab (R4). Collab decides who may see it. */
export const collabMaterialHref = (m: Pick<ItemMaterial, 'groupId' | 'materialId'>): string =>
  `${COLLAB_ORIGIN}/g/${encodeURIComponent(m.groupId)}/m/${encodeURIComponent(m.materialId)}`;

/** New picks after the current attachments, each once, up to ten (R1). */
export function addAttachments<T extends Pick<ItemMaterial, 'materialId'>>(current: readonly T[], picked: readonly T[]): T[] {
  const seen = new Set(current.map(m => m.materialId));
  const out = [...current];
  for (const m of picked) {
    if (out.length >= MAX_ITEM_MATERIALS) break;
    if (seen.has(m.materialId)) continue;
    seen.add(m.materialId);
    out.push(m);
  }
  return out;
}

/** One attachment a place up or down; the same list when the move would leave it. */
export function moveAttachment<T>(list: readonly T[], index: number, by: -1 | 1): readonly T[] {
  const to = index + by;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to]!, next[index]!];
  return next;
}

export type UploadFileRefusal = 'empty' | 'material_too_large' | 'unsupported_type';

/** The browser's cheap check before any byte moves: size and extension only (R5). */
export function uploadFileRefusal(file: { name: string; size: number }): UploadFileRefusal | null {
  if (file.size === 0) return 'empty';
  if (file.size > MAX_UPLOAD_FILE_BYTES) return 'material_too_large';
  return materialFormat(file.name) === 'other' ? 'unsupported_type' : null;
}

/**
 * Collab's refusal reasons as sentences (R7). The wording follows Collab's own
 * `UPLOAD_REFUSAL_COPY`, so a refused file reads the same in either app.
 */
const REFUSAL_TEXT: Readonly<Record<string, string>> = {
  material_too_large: 'That file is over 10 MB. Share a single document rather than a whole unit.',
  too_large: 'That file is over 10 MB. Share a single document rather than a whole unit.',
  unsupported_type: 'Collab takes PDF, Word, PowerPoint, PNG, JPEG and plain text files.',
  content_mismatch: 'That file does not match its type. Re-export it and try again.',
  empty: 'That file is empty.',
  office_macros: 'That file has macros. Save it again as .docx or .pptx without macros, then upload it.',
  office_linked_content: 'That file links to or embeds content from outside it. Remove linked or embedded objects, then upload it again.',
  material_limit_reached: 'Your My Planner group in Collab is full. Delete a material there to make room.',
  storage_limit_reached: 'Your My Planner group in Collab has used all of its storage. Delete a material there to make room.',
  rate_limited: 'That is a lot of uploads in a row. Wait a moment and try again.',
  length_required: 'The upload stopped before it finished. Try it again.',
  invalid_title: 'That file needs a shorter name.',
  collab_unavailable: 'Collab is unavailable right now. Try the upload again later.',
  unreachable: 'The upload stopped before it finished. Try it again.',
  request_timeout: 'The upload took too long. Try it again.',
};

/** A refusal reason as one sentence; any reason Collab adds later still reads as a refusal. */
export const uploadRefusalText = (reason: string): string =>
  REFUSAL_TEXT[reason] ?? 'Collab refused that file. Try a PDF, Word, PowerPoint, image or text file.';

/** A file's name without its extension, as Collab titles it; the whole name when that leaves nothing. */
export function uploadTitle(filename: string): string {
  const name = filename.trim();
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot).trim() : name;
  return (base || name).slice(0, MAX_UPLOAD_TITLE);
}
